import { gql } from '@apollo/client'
import { nullableConnectionNodes } from '@jusbrasil-web/shared-apollo-client/utilities'
import Script from 'next/script'
import { useEffect, useState } from 'react'

import { QualtricsDataProps } from '../../types'
import { QualtricsScript_ViewerFragment } from './__generated__/qualtrics-script.graphql'
import { HOTJAR_BADGE_ID, LOGGED_PLAN_ID } from './constants'

export interface QualtricsScriptProps {
  viewer: QualtricsScript_ViewerFragment | null
}

const isHotjarParticipant = ({ viewer }: QualtricsScriptProps) => {
  const badges = nullableConnectionNodes(viewer?.badges)
  return badges.some((badge) => badge.bid === HOTJAR_BADGE_ID)
}

export const QualtricsScript = (props: QualtricsScriptProps) => {
  const { viewer } = props
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    if (!viewer?.pid) {
      return
    }
    if (typeof window.QSI === 'undefined') {
      window.QSI = {
        config: {
          externalReference: viewer.pid,
        },
      }
    }
    const currentSubscriptionCreatedAt = viewer.subscriptions?.find(
      (sub) => sub?.type === 'SUBSCRIPTION' && sub?.plan?.planId !== LOGGED_PLAN_ID
    )?.createdAt

    const qualtricsData: QualtricsDataProps = {
      createdAt: viewer.createdAt ? new Date(viewer.createdAt).toISOString() : undefined,
      username: viewer.username,
      pid: viewer.pid,
      isAdmin: !!viewer?.user?.isAdmin,
      isPro: !!viewer.hasPaidPlan,
      isLawyer: !!viewer.isLawyer,
      isLawOperator: !!viewer.isLawOperator,
      hasDoctrine: !!viewer.hasDoctrine,
      isHotjarParticipant: isHotjarParticipant({ viewer }),
      currentSubscriptionCreatedAt: currentSubscriptionCreatedAt
        ? new Date(currentSubscriptionCreatedAt)
        : undefined,
      occupation: viewer.occupation?.title,
      intention:
        viewer.intentions && viewer.intentions.length > 0 ? viewer.intentions[0] : undefined,
    }

    if (viewer.plans?.[0]) {
      const currentPlan = viewer.plans[0]
      qualtricsData.planPrice = currentPlan.price
      qualtricsData.planName = currentPlan.name

      if (viewer.proSince) {
        qualtricsData.proSince = new Date(viewer.proSince).toISOString()
      }
    }
    window.qualtricsData = qualtricsData

    setIsLoaded(true)
  }, [viewer])

  if (!isLoaded) {
    return null
  }

  return (
    <>
      <Script id="qualtrics-script" strategy="lazyOnload">
        {`
(function(){var g=function(e,h,f,g){
this.get=function(a){for(var a=a+"=",c=document.cookie.split(";"),b=0,e=c.length;b<e;b++){for(var d=c[b];" "==d.charAt(0);)d=d.substring(1,d.length);if(0==d.indexOf(a))return d.substring(a.length,d.length)}return null};
this.set=function(a,c){var b="",b=new Date;b.setTime(b.getTime()+6048E5);b="; expires="+b.toGMTString();document.cookie=a+"="+c+b+"; path=/; "};
this.check=function(){var a=this.get(f);if(a)a=a.split(":");else if(100!=e)"v"==h&&(e=Math.random()>=e/100?0:100),a=[h,e,0],this.set(f,a.join(":"));else return!0;var c=a[1];if(100==c)return!0;switch(a[0]){case "v":return!1;case "r":return c=a[2]%Math.floor(100/c),a[2]++,this.set(f,a.join(":")),!c}return!0};
this.go=function(){if(this.check()){var a=document.createElement("script");a.type="text/javascript";a.src=g;document.body&&document.body.appendChild(a)}};
this.start=function(){var t=this;"complete"!==document.readyState?window.addEventListener?window.addEventListener("load",function(){t.go()},!1):window.attachEvent&&window.attachEvent("onload",function(){t.go()}):t.go()};};
try{(new g(100,"r","QSI_S_ZN_8jGNxau9tbgIxfM","https://zn8jgnxau9tbgixfm-jusbrasil.siteintercept.qualtrics.com/SIE/?Q_ZID=ZN_8jGNxau9tbgIxfM")).start()}catch(i){}})();
      `}
      </Script>
      <div id="ZN_8jGNxau9tbgIxfM"></div>
    </>
  )
}

QualtricsScript.fragments = {
  viewer: gql`
    fragment QualtricsScript_viewer on Profile {
      username
      pid
      createdAt
      isLawyer
      isLawOperator
      hasDoctrine
      hasPaidPlan
      proSince
      intentions
      user {
        isAdmin
      }
      plans {
        name
        price
      }
      badges(first: 10) {
        edges {
          node {
            bid
          }
        }
      }
      occupation {
        title
      }
      subscriptions {
        overdue
        createdAt
        type
        plan {
          planId
        }
      }
    }
  `,
}
